import { useEffect, useState } from 'react';
import { softphoneService } from './service';
import { useSoftphone } from '../store/softphone';
import { api } from '../lib/api';
import { cn } from '../lib/utils';
import { formatDuration, formatUKForDisplay } from '@marnic/shared';

export function ActiveCall(): JSX.Element {
  const currentCall = useSoftphone((s) => s.currentCall);
  const currentLead = useSoftphone((s) => s.currentLead);
  const isMuted = useSoftphone((s) => s.isMuted);
  const isOnHold = useSoftphone((s) => s.isOnHold);
  const callStartedAt = useSoftphone((s) => s.callStartedAt);
  const status = useSoftphone((s) => s.status);
  const setMuted = useSoftphone((s) => s.setMuted);
  const setHold = useSoftphone((s) => s.setHold);

  const [showKeypad, setShowKeypad] = useState(false);
  const [notes, setNotes] = useState('');
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!callStartedAt) return;
    setDuration(Math.floor((Date.now() - callStartedAt) / 1000));
    const id = setInterval(() => setDuration(Math.floor((Date.now() - callStartedAt) / 1000)), 1000);
    return () => clearInterval(id);
  }, [callStartedAt]);

  useEffect(() => {
    setNotes(currentCall?.notes ?? '');
  }, [currentCall?.id, currentCall?.notes]);

  // Auto-save notes every 1.5s while typing.
  useEffect(() => {
    if (!currentCall) return;
    const id = setTimeout(() => {
      void api.patch(`/calls/${currentCall.id}/notes`, { notes }).catch(() => {});
    }, 1500);
    return () => clearTimeout(id);
  }, [notes, currentCall?.id]);

  const headerName = currentLead
    ? `${currentLead.firstName} ${currentLead.lastName}`
    : currentCall
      ? formatUKForDisplay(currentCall.toNumber)
      : '—';

  return (
    <div className="card p-3 sm:p-4 space-y-3 sm:space-y-4">
      <div>
        <div className="text-base font-semibold truncate">{headerName}</div>
        {currentLead?.company && (
          <div className="text-xs text-ink-500 truncate">{currentLead.company}</div>
        )}
        {currentCall?.fromNumber && (
          <div className="text-xs text-ink-500 truncate">Calling from: <span className="font-mono">{formatUKForDisplay(currentCall.fromNumber)}</span></div>
        )}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-base font-mono font-semibold tabular-nums">
            {status === 'connecting' ? 'Ringing…' : formatDuration(duration)}
          </span>
          <span className="badge bg-red-100 text-red-700 shrink-0">● Recording</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
        <button
          type="button"
          className={cn(
            'min-w-0 truncate py-2.5 sm:py-2 text-xs sm:text-sm',
            isMuted ? 'btn-danger' : 'btn-secondary',
          )}
          onClick={() => {
            const next = !isMuted;
            softphoneService.mute(next);
            setMuted(next);
          }}
        >
          {isMuted ? '🔇 Muted' : '🎙 Mute'}
        </button>
        <button
          type="button"
          className="btn-secondary min-w-0 truncate py-2.5 sm:py-2 text-xs sm:text-sm"
          onClick={() => setHold(!isOnHold)}
          disabled
          title="Hold not yet wired in mock mode"
        >
          {isOnHold ? '▶ Resume' : '⏸ Hold'}
        </button>
        <button
          type="button"
          className="btn-secondary min-w-0 truncate py-2.5 sm:py-2 text-xs sm:text-sm"
          onClick={() => setShowKeypad((s) => !s)}
        >
          🔢 Keys
        </button>
      </div>

      {showKeypad && (
        <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
          {['1','2','3','4','5','6','7','8','9','*','0','#'].map((k) => (
            <button
              key={k}
              type="button"
              className="min-w-0 rounded-md border border-ink-200 py-2.5 sm:py-2 text-base sm:text-sm font-medium hover:bg-ink-100"
              onClick={() => softphoneService.sendDigits(k)}
            >
              {k}
            </button>
          ))}
        </div>
      )}

      <div>
        <label className="label">Live notes</label>
        <textarea
          className="input min-h-[72px] sm:min-h-[96px] lg:min-h-[120px]"
          placeholder="Notes save automatically every couple of seconds…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </div>
  );
}
